type ThrottleHeaderValue = string | number | null | undefined;

interface ThrottleHeaders {
  'gs-throttle-remaining-sec'?: ThrottleHeaderValue;
  'GS-THROTTLE-REMAINING-SEC'?: ThrottleHeaderValue;
  [key: string]: ThrottleHeaderValue;
}

type ThrottleResponseData = string | { message?: string } | null | undefined;

interface ThrottleResponse {
  status?: number;
  headers?: ThrottleHeaders;
  data?: ThrottleResponseData;
}

interface ThrottleAxiosErrorLike {
  response?: ThrottleResponse;
}

type ThrottleSourceError = ThrottleAxiosErrorLike | Error | null | undefined;

function hasThrottleResponse(error: ThrottleSourceError): error is ThrottleAxiosErrorLike {
  return Boolean(error && typeof error === 'object' && 'response' in error);
}

export interface ThrottleDetails {
  isThrottled: true;
  remainingSec: number;
  message: string;
}

export interface ThrottleError extends Error {
  isThrottled: true;
  remainingSec: number;
  originalError: ThrottleSourceError;
  response?: ThrottleResponse;
}

function parseRemainingSeconds(rawValue: ThrottleHeaderValue): number {
  const parsed = Number.parseInt(String(rawValue), 10);

  if (Number.isNaN(parsed)) {
    return 30;
  }

  if (parsed < 0) {
    return 0;
  }

  return parsed;
}

function extractMessage(data: ThrottleResponseData): string {
  if (!data) {
    return '';
  }

  if (typeof data === 'string') {
    return data;
  }

  if (typeof data === 'object' && typeof data.message === 'string') {
    return data.message;
  }

  return '';
}

export function formatDurationShort(totalSeconds: number | string): string {
  const parsed = Number.parseInt(String(totalSeconds), 10);
  const normalized = Number.isNaN(parsed) ? 0 : Math.max(0, parsed);

  const hours = Math.floor(normalized / 3600);
  const minutes = Math.floor((normalized % 3600) / 60);
  const seconds = normalized % 60;

  if (hours > 0) {
    if (minutes === 0 && seconds === 0) {
      return `${hours}h`;
    }
    if (seconds === 0) {
      return `${hours}h ${minutes}m`;
    }
    return `${hours}h ${minutes}m ${seconds}s`;
  }

  if (minutes > 0) {
    if (seconds === 0) {
      return `${minutes}m`;
    }
    return `${minutes}m ${seconds}s`;
  }

  return `${seconds}s`;
}

export function parseThrottleFromAxiosError(error: ThrottleSourceError): ThrottleDetails | null {
  if (!hasThrottleResponse(error)) {
    return null;
  }

  const response = error.response;

  if (!(response && response.status === 429)) {
    return null;
  }

  const headers = response.headers || {};
  const headerValue = headers['gs-throttle-remaining-sec'] || headers['GS-THROTTLE-REMAINING-SEC'];
  const remainingSec = parseRemainingSeconds(headerValue);
  const message = extractMessage(response.data);

  return {
    isThrottled: true,
    remainingSec,
    message,
  };
}

export function toThrottleError(error: ThrottleSourceError): ThrottleError | null {
  const throttled = parseThrottleFromAxiosError(error);

  if (!throttled) {
    return null;
  }

  const throttleError = new Error(throttled.message || 'Request throttled') as ThrottleError;
  throttleError.name = 'ThrottleError';
  throttleError.isThrottled = true;
  throttleError.remainingSec = throttled.remainingSec;
  throttleError.message = throttled.message || throttleError.message;
  throttleError.originalError = error;
  throttleError.response = (error as ThrottleAxiosErrorLike | null | undefined)?.response;

  return throttleError;
}